/**
 * Row model for the shared exceptions list — the one presentation of the three-level exception model
 * (task skips/extras, line blackouts, plan blackouts) used by the plan, line, and task screens.
 *
 * Rows are pre-flattened by the host rather than derived here: only the host knows which level it is
 * showing and which rows the user may remove, and a list that reached back into a plan document to
 * work that out would have to know about three different record shapes.
 */

import { CalendarDate } from '@workbuddy/ui/v2/components/calendar';
import { ScheduleExceptions, ScheduleExtraDate, ScheduleSkip } from '@jms/model/scheduling';

/** Whether the row removes work (a skip or a blackout) or adds it (an extra date). */
export type ExceptionRowKind = 'skip' | 'add';

/** Which document owns the row. Absent means the record on screen owns it, so it carries no chip. */
export type ExceptionRowOrigin = 'plan' | 'line' | 'task';

export interface ExceptionRow {
  readonly id: string;
  readonly kind: ExceptionRowKind;
  /** A single day. Mutually exclusive with `from`/`to`. */
  readonly date?: CalendarDate;
  readonly from?: CalendarDate;
  readonly to?: CalendarDate;
  readonly reason?: string;
  /** Set only for rows inherited from a level above; those render locked with an origin chip. */
  readonly origin?: ExceptionRowOrigin;
  readonly removable?: boolean;
}

/** `Plan` / `Line` — the chip text beside a locked inherited row. */
export const EXCEPTION_ORIGIN_LABELS: Record<ExceptionRowOrigin, string> = {
  plan: 'Plan',
  line: 'Line',
  task: 'Task',
};

/**
 * Task-screen flattening: the task's own skips/extras, then inherited plan and line blackouts.
 *
 * Owned rows carry a remove control unless the host is read-only. Inherited rows are locked — they are
 * edited on the plan or line, not here.
 */
export function scheduleExceptionRows(exceptions: ScheduleExceptions | undefined, options?: { removable?: boolean }): ExceptionRow[] {

  const removable = options?.removable !== false;
  const rows: ExceptionRow[] = [];

  for (const skip of exceptions?.skips ?? []) {
    rows.push({
      id: ownedSkipId(skip),
      kind: 'skip',
      ...skipSpan(skip),
      ...(skip.reason ? { reason: skip.reason } : {}),
      removable,
    });
  }

  for (const extra of exceptions?.extras ?? []) {
    rows.push({
      id: ownedExtraId(extra),
      kind: 'add',
      date: extra.date,
      ...(extra.note ? { reason: extra.note } : {}),
      removable,
    });
  }

  rows.push(...inheritedSkipRows(exceptions?.planBlackouts, 'plan'));
  rows.push(...inheritedSkipRows(exceptions?.lineBlackouts, 'line'));

  return rows;
}

/** Drops an owned skip or extra by list-row id. Inherited ids never match, so those rows stay. */
export function removeScheduleException(exceptions: ScheduleExceptions | undefined, id: string): ScheduleExceptions {

  const current = exceptions ?? {};

  return {
    ...current,
    skips: current.skips?.filter(skip => ownedSkipId(skip) !== id),
    extras: current.extras?.filter(extra => ownedExtraId(extra) !== id),
  };
}

function ownedSkipId(skip: ScheduleSkip): string {

  return skip.date ? `skip:${skip.date}` : `skip:${skip.from ?? ''}:${skip.to ?? ''}`;
}

function ownedExtraId(extra: ScheduleExtraDate): string {

  return `extra:${extra.date}`;
}

function skipSpan(skip: ScheduleSkip): Pick<ExceptionRow, 'date' | 'from' | 'to'> {

  return skip.date ? { date: skip.date } : { from: skip.from, to: skip.to };
}

function inheritedSkipRows(skips: readonly ScheduleSkip[] | undefined, origin: ExceptionRowOrigin): ExceptionRow[] {

  return (skips ?? []).map((skip, index) => ({
    id: `${origin}:${ownedSkipId(skip)}:${index}`,
    kind: 'skip' as const,
    ...skipSpan(skip),
    ...(skip.reason ? { reason: skip.reason } : {}),
    origin,
    removable: false,
  }));
}
